import { useForm as useHookForm, FormProvider } from 'react-hook-form';
import { BenchmarkForm } from '../../components/BenchmarkMetadata/components/BenchmarkForm/BenchmarkForm';
import { CompositeReturnTimeWindow } from './CompositeReturnTimeWindow';
import { useTable, useTimeWindow } from './hooks';
import {
  BenchmarkScreen,
  useBenchmarkDetails,
  useTWNavigation,
  useTWScreenParams,
  useTimeWindows,
} from 'hooks';
import { Footer, TableTabbed } from 'components';
import { useYupValidationResolver } from '../../helpers/form';
import { DevTool } from '@hookform/devtools';
import {
  CompositeReturnValidationScheme,
  compositeReturnTimeWindowShape,
} from './CompositeReturnValidationScheme';
import { useSubmit } from 'hooks/useSubmit';
import { getBody } from './CompositeReturnHelpers';
import { showSuccess } from 'helpers/showSuccess';
import { useCallback, useEffect, useState } from 'react';
import {
  CompositeReturn,
  CompositeReturnTimeWindow as TimeWindow,
} from 'types/CompositeReturn';
import { BenchmarkMetadata } from 'components/BenchmarkMetadata/BenchmarkMetadata';
import { SmallDisplay } from './components/SmallDisplay';
import { DefaultDisplay } from './components/DefaultDisplay';
import { TimeWindowsEditTitle, TimeWindows } from 'components/TimeWindow';
import { getToDates } from 'helpers/timeWindow/getToDates';
import { BenchmarkTypes } from 'types/Benchmark';
import { BenchmarkFormTitle } from 'components/BenchmarkMetadata/components/BenchmarkForm/BenchmarkFormStyles';
import { useProblemsHandler } from 'modules/BenchmarkForm/hooks/useProblemsHandler';
import { LoadingWrapper } from 'components/LoadingWrapper/LoadingWrapper';

export const BenchmarkCompositeReturn = () => {
  const [isFormLoadComplete, setIsFormLoadComplete] = useState(false);
  const resolver = useYupValidationResolver(CompositeReturnValidationScheme());

  const methods = useHookForm<any>({
    resolver,
    defaultValues: {
      baseCurrencyCode: 'USD',
    },
  });

  const {
    handleSubmit,
    formState: { errors, isDirty },
    control,
    reset,
    setValue,
    setError,
  } = methods;

  const { timeWindows, timeWindowsFilled } = useTimeWindows<TimeWindow>({
    setValue,
    control,
    timeWindowSchema: compositeReturnTimeWindowShape,
  });

  const {
    benchmark,
    refetch,
    isLoading: isLoadingBenchmark,
  } = useBenchmarkDetails<CompositeReturn>();

  const { isTimeWindowsScreen, addTimeWindow, benchmarkScreen } =
    useTWScreenParams(benchmark?.id);

  const handleTWNavigation = useTWNavigation(benchmarkScreen);

  const tableProps = useTable(timeWindows);

  const {
    onSubmit,
    isSuccess,
    isLoading: isSubmitting,
    errors: problemDetails,
  } = useSubmit({
    originalValues: benchmark,
    getBody,
    originalTimeWindows: timeWindows,
  });

  useProblemsHandler(setError, problemDetails);

  useEffect(() => {
    if (isSuccess) {
      if (benchmark?.id) {
        showSuccess('Benchmark was successfully saved');
        refetch();
        handleTWNavigation();
        window.scrollTo(0, 0);
      }
    }
  }, [isSuccess, benchmark?.id]);

  const resetData = useCallback(() => {
    reset();
    if (benchmark) {
      let values: any = {
        ...benchmark,
        timeWindows: getToDates(
          benchmark?.timeWindows?.map((item: any) => {
            return {
              fromDate: new Date(item.fromDate),
              assetSelectionSetId: item.assetSelectionSetId,
              tagRefPropertyDefinitionId: item.tagRefPropertyDefinitionId,
              tagId: item.tagId + '',
            };
          })
        ),
      };

      reset(values);
    }
  }, [benchmark, reset]);

  useEffect(() => {
    resetData();
  }, [resetData]);

  const tableTabbedProps = {
    ...tableProps,
    useTimeWindow,
    handleSubmit,
    onSubmit,
    isLoading: isSubmitting || isLoadingBenchmark,
    benchmarkName: benchmark?.name,
    benchmarkId: benchmark?.id,
  };

  const renderContent = () => {
    if (isLoadingBenchmark) return null;

    if (isTimeWindowsScreen || addTimeWindow) {
      return (
        <TimeWindows
          benchmarkType={BenchmarkTypes.CompositeReturn}
          timeWindowsFilled={timeWindowsFilled}
          benchmarkId={benchmark?.id}
          TimeWindowComponent={CompositeReturnTimeWindow}
          defaultValues={{
            fromDate: new Date(),
            assetSelectionSetId: '',
            tagRefPropertyDefinitionId: '',
            tagId: '',
          }}
        />
      );
    }

    return <TableTabbed {...tableTabbedProps} />;
  };

  return (
    <LoadingWrapper isLoading={isLoadingBenchmark && isFormLoadComplete}>
      <TimeWindowsEditTitle isTimeWindowsScreen={isTimeWindowsScreen} />
      {benchmark?.id && !isSubmitting ? (
        <BenchmarkMetadata
          benchmark={benchmark}
          onConfirm={refetch}
          isTimeWindowsScreen={isTimeWindowsScreen}
        >
          <SmallDisplay benchmark={benchmark} />
          <DefaultDisplay benchmark={benchmark} />
        </BenchmarkMetadata>
      ) : null}
      <FormProvider {...methods}>
        {benchmark === undefined ? (
          <div style={{ padding: 20 }}>
            <BenchmarkFormTitle>
              Add New Composite Return Benchmark
            </BenchmarkFormTitle>
            <BenchmarkForm
              benchmark={benchmark}
              benchmarkType={BenchmarkTypes.CompositeReturn}
              onLoadComplete={() => setIsFormLoadComplete(true)}
            />
          </div>
        ) : null}
        {renderContent()}
        <Footer
          showReturnHome={benchmarkScreen === BenchmarkScreen.editBenchmark}
          onSave={handleSubmit((value: any) => {
            onSubmit({
              ...value,
              onSaveSuccess: isSuccess,
            });
          })}
          onDiscardChanges={() => {
            resetData();
            handleTWNavigation();
            window.scrollTo(0, 0);
          }}
          isSaveDisabled={!isDirty || errors.timeWindows}
          isLoading={tableTabbedProps.isLoading}
          showEditButtons={Boolean(isTimeWindowsScreen || !benchmark?.id)}
          discardText='Cancel'
          isDirty={isDirty}
        />
        <DevTool control={control} placement='top-left' />
      </FormProvider>
    </LoadingWrapper>
  );
};
